import { ref, computed, watch } from 'vue';
import * as Sentry from '@sentry/vue';
import type { Ref, ComputedRef } from 'vue';
import type { KycCategory, KycCheck, KycEntity } from '@/types/kyc';
import parecerApi, { type Parecer, type ParecerDraftPatch, type ParecerDecisionPatch } from '@/api/modules/parecerApi';
import type { ManifestFieldConfig } from '@/api/modules/parecerApi';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';

// --- Type Definitions ---

export type DecisionOutcome =
  | 'draft'
  | 'submitted'
  | 'approved'
  | 'approved_with_conditions'
  | 'rfi'
  | 'escalated'
  | 'rejected';

export type AnalystRiskLevel = 'low' | 'medium' | 'high' | 'very_high';

export interface FlagComment {
  categorySlug: string;
  comment: string;
}

// Per-entity flag comment for granular tracking
export interface EntityFlagComment {
  categorySlug: string;
  entityTaxId: string;
  entityName: string;
  comment: string;
}

// Flagged entity with category data
export interface FlaggedEntity {
  entityTaxId: string;
  entityName: string;
  entityType: string;
  relationship: string;
  riskLevelValue: number | null;
  riskLevelText: string | null;
  category: KycCategory | null;
}

// Flag category with its flagged entities
export interface FlagWithEntities {
  id: number;
  title: string;
  category_slug: string;
  entitiesChecked: number;
  entitiesFailed: number;
  status: string;
  isOutdated: boolean;
  lastUpdated: string | null;
  highestRiskLevelValue?: number | null;
  highestRiskLevel?: string | null;
  flaggedEntities: FlaggedEntity[];
}

export interface MitigationCondition {
  id: string;
  description: string;
  owner?: string;
  dueDate?: string;
}

export interface Attachment {
  id: string;
  fileId?: string;
  associationId?: string;
  name: string;
  sizeBytes?: number;
  localFile?: File;
  url: string;
  uploadedAt: string;
  uploadedBy?: string | null;
  description?: string | null;
  categorySlug?: string | null;
  changeStatus?: 'added' | 'deleted';
}

export interface DecisionDraft {
  // Core decision fields
  decision: DecisionOutcome;
  requestForInformation: boolean;
  reasonCode: string;
  secondaryReasonCode?: string;
  justification: string;

  // Analyst risk classification
  analystRiskLevel: AnalystRiskLevel | null;
  analystRiskJustification: string;

  // Mitigations/Conditions (for conditional approvals)
  conditions: MitigationCondition[];

  // Per-flag comments (required for approval paths with failed/attention categories)
  flagComments: FlagComment[];

  // Per-entity per-flag comments (granular tracking for wizard flow)
  entityFlagComments: EntityFlagComment[];

  // Governance
  expirationDate: string | null;

  // Attachments (mock)
  attachments: Attachment[];

  // Relationship context (mock)
  relationshipTypes: string[];
  relationshipIdentifier: string;

  // Timestamps
  createdAt: string;
  lastSavedAt: string | null;
}

// --- Reason Codes (pt-BR) ---

// Reason code stamped on automated (policy) decisions by the backend. It is shown as a
// read-only label when viewing an automated parecer, but is NEVER offered as a selectable
// option — it is excluded from every REASON_CODES_BY_OUTCOME whitelist and from the
// unknown-outcome fallback in getValidReasonCodeValuesForOutcome below.
export const POLICY_DECISION_REASON_CODE = 'policy_decision';

export const REASON_CODES = [
  { value: 'no_matches_found', label: 'Nenhuma correspondência encontrada' },
  { value: 'issues_mitigated', label: 'Correspondências identificadas e mitigadas' },
  { value: 'incomplete_documentation', label: 'Documentação incompleta' },
  { value: 'high_risk_unmitigable', label: 'Risco alto não mitigável' },
  { value: 'complex_corporate_structure', label: 'Estrutura societária complexa' },
  { value: 'other', label: 'Outro (especificar na justificativa)' },
  { value: POLICY_DECISION_REASON_CODE, label: 'Decisão automática da política' },
] as const;

// --- Reason Codes by Outcome ---
// Maps each decision decision to the valid reason codes

export const REASON_CODES_BY_OUTCOME: Record<DecisionOutcome, string[]> = {
  draft: [],
  submitted: ['no_matches_found', 'issues_mitigated', 'complex_corporate_structure', 'other'],
  approved: ['no_matches_found', 'issues_mitigated', 'other'],
  approved_with_conditions: ['issues_mitigated', 'complex_corporate_structure', 'other'],
  rfi: ['incomplete_documentation', 'complex_corporate_structure', 'other'],
  escalated: ['complex_corporate_structure', 'high_risk_unmitigable', 'other'],
  rejected: ['high_risk_unmitigable', 'incomplete_documentation', 'other'],
};

const hasKnownReasonCodeMapping = (decision: string): decision is DecisionOutcome =>
  Object.prototype.hasOwnProperty.call(REASON_CODES_BY_OUTCOME, decision);

// Returns allowed reason-code values for an decision.
// For dynamic workflow stage slugs (unknown outcomes), fallback to full enum.
export const getValidReasonCodeValuesForOutcome = (decision: string | null | undefined): string[] => {
  if (!decision || decision === 'draft') return [];
  if (hasKnownReasonCodeMapping(decision)) {
    return REASON_CODES_BY_OUTCOME[decision];
  }
  return REASON_CODES.map((code) => code.value);
};

// Helper to get filtered reason codes for a given decision
export const getReasonCodesForOutcome = (decision: string | null | undefined) => {
  const validCodes = getValidReasonCodeValuesForOutcome(decision);
  return REASON_CODES.filter((code) => validCodes.includes(code.value));
};

// --- Outcome Labels (pt-BR) ---

export const OUTCOME_LABELS: Record<DecisionOutcome, string> = {
  draft: 'Rascunho',
  submitted: 'Enviado',
  approved: 'Aprovado',
  approved_with_conditions: 'Aprovado com condições',
  rfi: 'Solicitar informações (RFI)',
  escalated: 'Escalado',
  rejected: 'Reprovado',
};

// --- Risk Level Labels (pt-BR) ---

export const RISK_LEVEL_LABELS: Record<AnalystRiskLevel, string> = {
  // very_low: 'Muito Baixo',
  low: 'Baixo',
  medium: 'Médio',
  high: 'Alto',
  very_high: 'Muito Alto',
};

// --- Composable Interface ---

interface UseKycDecisionDraftOptions {
  requestId: Ref<string | null>;
  parecerId?: Ref<string | null>;
  relationshipTypes?: Ref<string[]>;
  kycChecks?: Ref<KycCheck[]>;
  entities?: Ref<KycEntity[]>;
  reportRiskLevel?: Ref<number | null>;
  entityType?: Ref<'person' | 'company'>;
  entityTaxId?: Ref<string>;
  entityName?: Ref<string>;
  manifestFields?: Ref<Record<string, ManifestFieldConfig> | null> | ComputedRef<Record<string, ManifestFieldConfig> | null>;
}

export interface ValidationError {
  field: string;
  message: string;
}

// --- Composable ---

export function useKycDecisionDraft(options: UseKycDecisionDraftOptions) {
  const {
    requestId,
    parecerId,
    relationshipTypes,
    kycChecks,
    entities,
    entityType,
    entityTaxId,
    entityName,
    manifestFields,
  } = options;

  // --- State ---

  const draft: Ref<DecisionDraft> = ref(createEmptyDraft());
  const isSaving: Ref<boolean> = ref(false);
  const hasUnsavedChanges: Ref<boolean> = ref(false);
  const currentParecerId: Ref<string | null> = ref(parecerId?.value ?? null);
  const currentParecer: Ref<Parecer | null> = ref(null);
  const apiError: Ref<string | null> = ref(null);
  const useLocalStorageFallback: Ref<boolean> = ref(false);

  // --- Computed: Categories that need attention ---

  const flagsNeedingAttention: ComputedRef<KycCheck[]> = computed(() => {
    if (!kycChecks?.value) return [];
    return kycChecks.value.filter(
      (check) => check.status === 'failed' || (
        typeof check.highestRiskLevelValue === 'number' && check.highestRiskLevelValue > 0
      )
    );
  });

  const hasRedFlagRisk = (levelValue: unknown): levelValue is number => (
    typeof levelValue === 'number' && levelValue > 0
  );

  // --- Computed: Flagged entities per category ---

  const flagsWithEntities: ComputedRef<FlagWithEntities[]> = computed(() => {
    if (!flagsNeedingAttention.value.length || !entities?.value) return [];

    return flagsNeedingAttention.value.map((flag) => {
      const flaggedEntities: FlaggedEntity[] = [];

      entities.value.forEach((entity) => {
        const category = entity.categories.find((c) => c.category_slug === flag.category_slug);
        if (category && category.is_relevant !== false && hasRedFlagRisk(category.level_value)) {
          flaggedEntities.push({
            entityTaxId: entity.entity_tax_id,
            entityName: entity.name,
            entityType: entity.entity_type,
            relationship: entity.relationship,
            riskLevelValue: category.level_value,
            riskLevelText: category.level_text,
            category,
          });
        }
      });

      return {
        ...flag,
        flaggedEntities,
      };
    }).filter((flag) => flag.flaggedEntities.length > 0);
  });

  const isManifestFieldRequired = (fieldId: string): boolean => {
    const field = manifestFields?.value?.[fieldId];
    return !!field?.visible && field.required;
  };

  // --- Computed: Validation ---

  const validationErrors: ComputedRef<ValidationError[]> = computed(() => {
    const errors: ValidationError[] = [];
    const d = draft.value;

    if (d.requestForInformation) return errors;

    if (isManifestFieldRequired('decision_proposal') && d.decision === 'draft') {
      errors.push({ field: 'decision', message: 'Resultado da análise é obrigatório' });
    }

    // Skip dependent validation until a decision is selected
    if (d.decision === 'draft') return errors;

    // Manifest-driven required fields
    if (isManifestFieldRequired('reason_code_proposal') && !d.reasonCode) {
      errors.push({ field: 'reasonCode', message: 'Motivo principal é obrigatório' });
    }

    if (isManifestFieldRequired('justification_proposal') && !d.justification.trim()) {
      errors.push({ field: 'justification', message: 'Comentário/justificativa é obrigatório' });
    }

    if (isManifestFieldRequired('expiration_date_proposal') && !d.expirationDate) {
      errors.push({ field: 'expirationDate', message: 'Data de expiração é obrigatória nesta etapa' });
    }

    // Require one comment per rendered entity/category red-flag target.
    if (
      isManifestFieldRequired('flag_comments')
      && flagsWithEntities.value.length > 0
    ) {
      const missingEntityComments: string[] = [];
      flagsWithEntities.value.forEach((flag) => {
        flag.flaggedEntities.forEach((entity) => {
          const hasComment = d.entityFlagComments.some(
            (efc) =>
              efc.categorySlug === flag.category_slug &&
              efc.entityTaxId === entity.entityTaxId &&
              efc.comment.trim()
          );
          if (!hasComment) {
            missingEntityComments.push(`${entity.entityName} (${flag.title})`);
          }
        });
      });
      if (missingEntityComments.length > 0) {
        errors.push({
          field: 'entityFlagComments',
          message: `Comentários obrigatórios para entidades com alertas: ${missingEntityComments.slice(0, 3).join(', ')}${missingEntityComments.length > 3 ? ` e mais ${missingEntityComments.length - 3}` : ''}`,
        });
      }
    }

    if (isManifestFieldRequired('risk_justification_proposal') && !d.analystRiskJustification.trim()) {
      errors.push({
        field: 'analystRiskJustification',
        message: 'Justificativa de risco é obrigatória nesta etapa',
      });
    }

    // Conditions required for approved_with_conditions
    if (d.decision === 'approved_with_conditions' && d.conditions.length === 0) {
      errors.push({
        field: 'conditions',
        message: 'Ao menos uma condição é obrigatória para aprovação condicional',
      });
    }

    return errors;
  });

  const isValid: ComputedRef<boolean> = computed(() => validationErrors.value.length === 0);

  const canSubmit: ComputedRef<boolean> = computed(() => {
    return draft.value.requestForInformation || (draft.value.decision !== 'draft' && isValid.value);
  });

  // --- Methods ---

  function createEmptyDraft(): DecisionDraft {
    return {
      decision: 'draft',
      requestForInformation: false,
      reasonCode: '',
      secondaryReasonCode: '',
      justification: '',
      analystRiskLevel: null,
      analystRiskJustification: '',
      conditions: [],
      flagComments: [],
      entityFlagComments: [],
      expirationDate: null,
      attachments: [],
      relationshipTypes: relationshipTypes?.value || [],
      relationshipIdentifier: '',
      createdAt: new Date().toISOString(),
      lastSavedAt: null,
    };
  }

  function getStoragePrefix(): string | null {
    if (!requestId.value) return null;
    return `kyc-decision-draft-${requestId.value}`;
  }

  function getStorageKey(): string | null {
    const prefix = getStoragePrefix();
    if (!prefix) return null;
    return `${prefix}-latest`;
  }

  function getLegacyStorageKeys(): string[] {
    const prefix = getStoragePrefix();
    if (!prefix) return [];
    const legacyPrefix = `${prefix}-`;
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(legacyPrefix) && key !== getStorageKey()) {
        keys.push(key);
      }
    }
    return keys;
  }

  function loadFromStorage(): boolean {
    const key = getStorageKey();
    if (!key) return false;

    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        draft.value = { ...createEmptyDraft(), ...parsed };
        hasUnsavedChanges.value = false;
        return true;
      }

      const legacyKeys = getLegacyStorageKeys();
      if (!legacyKeys.length) return false;

      let latestDraft: DecisionDraft | null = null;
      let latestTimestamp = 0;

      legacyKeys.forEach((legacyKey) => {
        const raw = localStorage.getItem(legacyKey);
        if (!raw) return;
        try {
          const parsed = JSON.parse(raw);
          const merged = { ...createEmptyDraft(), ...parsed } as DecisionDraft;
          const ts = new Date(merged.lastSavedAt || merged.createdAt || 0).getTime();
          if (!latestDraft || ts >= latestTimestamp) {
            latestDraft = merged;
            latestTimestamp = Number.isNaN(ts) ? 0 : ts;
          }
        } catch {
          // Ignore malformed legacy entry and continue migration.
        }
      });

      if (!latestDraft) return false;

      draft.value = latestDraft;
      hasUnsavedChanges.value = false;
      localStorage.setItem(key, JSON.stringify(draft.value));
      legacyKeys.forEach((legacyKey) => localStorage.removeItem(legacyKey));
      return true;
    } catch (e) {
      console.warn('Failed to load draft from storage:', e);
    }
    return false;
  }

  function saveToStorage(): void {
    const key = getStorageKey();
    if (!key) return;

    try {
      draft.value.lastSavedAt = new Date().toISOString();
      const storedDraft = {
        ...draft.value,
        attachments: draft.value.attachments.filter((attachment) => !attachment.localFile),
      };
      localStorage.setItem(key, JSON.stringify(storedDraft));
      hasUnsavedChanges.value = draft.value.attachments.some(
        (attachment) => !!attachment.localFile || attachment.changeStatus === 'deleted'
      );
    } catch (e) {
      console.warn('Failed to save draft to storage:', e);
    }
  }

  function clearStorage(): void {
    const key = getStorageKey();
    if (key) {
      localStorage.removeItem(key);
    }

    const prefix = getStoragePrefix();
    if (!prefix) return;

    const legacyPrefix = `${prefix}-`;
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const storageKey = localStorage.key(i);
      if (storageKey && storageKey.startsWith(legacyPrefix)) {
        keysToRemove.push(storageKey);
      }
    }
    keysToRemove.forEach((storageKey) => localStorage.removeItem(storageKey));
  }

  function resetDraft(): void {
    draft.value = createEmptyDraft();
    hasUnsavedChanges.value = false;
    currentParecerId.value = null;
    currentParecer.value = null;
  }

  function draftToApiPatch(): ParecerDraftPatch {
    const shouldClearDecision = draft.value.requestForInformation;
    const decisionPatch: ParecerDecisionPatch = {
      decision: shouldClearDecision || draft.value.decision === 'draft' ? null : draft.value.decision,
      reason_code: shouldClearDecision ? null : (draft.value.reasonCode || null),
      secondary_reason_code: shouldClearDecision ? null : (draft.value.secondaryReasonCode || null),
      justification: shouldClearDecision ? null : (draft.value.justification || null),
      risk_level: shouldClearDecision ? null : draft.value.analystRiskLevel,
      risk_justification: shouldClearDecision ? null : (draft.value.analystRiskJustification || null),
      expiration_date: shouldClearDecision ? null : draft.value.expirationDate,
    };

    const patch: ParecerDraftPatch = {
      decision: decisionPatch,
    };

    if (draft.value.relationshipTypes.length > 0) {
      patch.relationship_types = {
        relationship_type_slugs: [...draft.value.relationshipTypes],
      };
    }

    if (draft.value.entityFlagComments.length > 0) {
      patch.flag_comments = {
        action_type: 'add',
        items: draft.value.entityFlagComments.map((efc) => ({
          category_slug: efc.categorySlug,
          entity_tax_id: efc.entityTaxId,
          entity_name: efc.entityName,
          entity_type: entityType?.value || 'company',
          comment: efc.comment,
        })),
      };
    }

    return patch;
  }

  function parecerToDraft(parecer: Parecer): void {
    const proposalDecision = parecer.decisions?.proposal ?? null;

    draft.value = {
      decision: proposalDecision?.decision || 'draft',
      requestForInformation: false,
      reasonCode: proposalDecision?.reason_code || '',
      secondaryReasonCode: proposalDecision?.secondary_reason_code || '',
      justification: proposalDecision?.justification || '',
      analystRiskLevel: proposalDecision?.risk_level || null,
      analystRiskJustification: proposalDecision?.risk_justification || '',
      conditions: [], // TODO: Map from parecer if conditions are stored separately
      flagComments: [],
      entityFlagComments: parecer.flag_comments.map((fc) => ({
        categorySlug: fc.category_slug,
        entityTaxId: fc.entity_tax_id,
        entityName: fc.entity_name,
        comment: fc.comment,
      })),
      expirationDate: proposalDecision?.expiration_date || null,
      attachments: parecer.files.map((f) => ({
        id: f.id,
        fileId: f.file_id,
        associationId: f.id,
        name: f.display_name,
        sizeBytes: f.file_size_bytes ?? undefined,
        url: f.signed_url || '',
        uploadedAt: f.inserted_at,
        uploadedBy: f.inserted_by_user_name,
        description: f.description,
        categorySlug: f.category_slug,
      })),
      relationshipTypes: parecer.relationship_types.map((rt) => rt.slug).filter((s): s is string => !!s),
      relationshipIdentifier: '',
      createdAt: parecer.inserted_at,
      lastSavedAt: parecer.updated_at || parecer.inserted_at,
    };

    currentParecerId.value = parecer.id;
    currentParecer.value = parecer;
    hasUnsavedChanges.value = false;
  }

  async function loadFromApi(): Promise<boolean> {
    if (!requestId.value) return false;

    try {
      const parecer = await parecerApi.getParecerByRequestId(requestId.value);
      if (parecer) {
        parecerToDraft(parecer);
        useLocalStorageFallback.value = false;
        return true;
      }
    } catch (e: any) {
      console.warn('Failed to load parecer from API, falling back to localStorage:', e);
      apiError.value = getApiErrorMessage(e, 'Falha ao carregar o parecer.');
      useLocalStorageFallback.value = true;
    }
    return false;
  }

  async function saveToApi(): Promise<boolean> {
    if (!currentParecerId.value) {
      // No parecer exists yet - need to create one first
      if (!requestId.value || !entityType?.value || !entityTaxId?.value || !entityName?.value) {
        console.warn('Cannot create parecer: missing required entity info');
        return false;
      }

      try {
        const parecer = await parecerApi.createParecer({
          report_request_id: requestId.value,
          entity_type: entityType.value,
          entity_tax_id: entityTaxId.value,
          entity_name: entityName.value,
        });
        currentParecerId.value = parecer.id;
        currentParecer.value = parecer;
      } catch (e: any) {
        console.warn('Failed to create parecer:', e);
        apiError.value = getApiErrorMessage(e, 'Falha ao criar o parecer.');
        useLocalStorageFallback.value = true;
        saveToStorage();
        return false;
      }
    }

    try {
      const patch = draftToApiPatch();
      const pendingAttachments = draft.value.attachments.filter((attachment) => attachment.localFile);
      const files = pendingAttachments.flatMap((attachment) =>
        attachment.localFile ? [attachment.localFile] : []
      );
      const deletedFileIds = draft.value.attachments
        .filter((attachment) => attachment.changeStatus === 'deleted' && attachment.associationId)
        .map((attachment) => attachment.associationId!);
      const parecer = await parecerApi.saveDraft(
        currentParecerId.value,
        patch,
        files,
        deletedFileIds,
      );
      currentParecer.value = parecer;
      pendingAttachments.forEach((attachment) => URL.revokeObjectURL(attachment.url));
      parecerToDraft(parecer);
      apiError.value = null;
      return true;
    } catch (e: any) {
      console.warn('Failed to save draft to API, falling back to localStorage:', e);
      Sentry.captureException(e, {
        tags: { feature: 'parecer-upload', action: 'save-draft', http_status: e?.status },
        extra: {
          parecerId: currentParecerId.value,
          pendingFileCount: draft.value.attachments.filter((a) => a.localFile).length,
        },
      });
      apiError.value = getApiErrorMessage(e, 'Falha ao salvar o rascunho.');
      useLocalStorageFallback.value = true;
      saveToStorage();
      return false;
    }
  }

  async function saveDraft(): Promise<void> {
    isSaving.value = true;
    try {
      if (useLocalStorageFallback.value) {
        saveToStorage();
      } else {
        const saved = await saveToApi();
        if (!saved) {
          saveToStorage();
        }
      }
    } finally {
      isSaving.value = false;
    }
  }

  async function submitDecision(targetStageSlug?: string): Promise<boolean> {
    if (!currentParecerId.value) {
      console.warn('Cannot submit: no parecer ID');
      return false;
    }

    const submittedStageId = currentParecer.value?.current_stage?.id;
    if (!submittedStageId) {
      console.warn('Cannot submit: parecer has no current stage');
      apiError.value = 'Falha ao enviar o parecer.';
      return false;
    }

    isSaving.value = true;
    try {
      const draftPatch = draftToApiPatch();
      const payload = {
        workflow_stage_id: submittedStageId,
        target_stage_slug: draft.value.requestForInformation
          ? 'request_for_information'
          : targetStageSlug,
        draft_patch: draftPatch,
        deleted_file_ids: draft.value.attachments
          .filter((attachment) => attachment.changeStatus === 'deleted' && attachment.associationId)
          .map((attachment) => attachment.associationId!),
      };
      const files = draft.value.attachments.flatMap((attachment) =>
        attachment.localFile ? [attachment.localFile] : []
      );
      const parecer = files.length > 0
        ? await parecerApi.submitAnalystDecision(currentParecerId.value, payload, files)
        : await parecerApi.submitAnalystDecision(currentParecerId.value, payload);
      currentParecer.value = parecer;
      draft.value.attachments
        .filter((attachment) => attachment.localFile)
        .forEach((attachment) => URL.revokeObjectURL(attachment.url));

      // Clear localStorage draft after successful submission
      clearStorage();

      return true;
    } catch (e: any) {
      console.error('Failed to submit decision:', e);
      Sentry.captureException(e, {
        tags: { feature: 'parecer-upload', action: 'submit-decision', http_status: e?.status },
        extra: {
          parecerId: currentParecerId.value,
          pendingFileCount: draft.value.attachments.filter((a) => a.localFile).length,
        },
      });
      apiError.value = getApiErrorMessage(e, 'Falha ao enviar o parecer.');
      return false;
    } finally {
      isSaving.value = false;
    }
  }

  function setOutcome(decision: DecisionOutcome): void {
    draft.value.decision = decision;
    if (decision !== 'draft') {
      draft.value.requestForInformation = false;
    }
    hasUnsavedChanges.value = true;
  }

  function setRequestForInformation(requestForInformation: boolean): void {
    draft.value.requestForInformation = requestForInformation;
    if (requestForInformation) {
      draft.value.decision = 'draft';
      draft.value.reasonCode = '';
      draft.value.secondaryReasonCode = '';
    }
    hasUnsavedChanges.value = true;
  }

  function addCondition(): void {
    draft.value.conditions.push({
      id: crypto.randomUUID(),
      description: '',
    });
    hasUnsavedChanges.value = true;
  }

  function removeCondition(id: string): void {
    draft.value.conditions = draft.value.conditions.filter((c) => c.id !== id);
    hasUnsavedChanges.value = true;
  }

  function setFlagComment(categorySlug: string, comment: string): void {
    const existing = draft.value.flagComments.find((fc) => fc.categorySlug === categorySlug);
    if (existing) {
      existing.comment = comment;
    } else {
      draft.value.flagComments.push({ categorySlug, comment });
    }
    hasUnsavedChanges.value = true;
  }

  function getFlagComment(categorySlug: string): string {
    return draft.value.flagComments.find((fc) => fc.categorySlug === categorySlug)?.comment || '';
  }

  function setEntityFlagComment(
    categorySlug: string,
    entityTaxId: string,
    entityName: string,
    comment: string
  ): void {
    const existing = draft.value.entityFlagComments.find(
      (efc) => efc.categorySlug === categorySlug && efc.entityTaxId === entityTaxId
    );
    if (existing) {
      existing.comment = comment;
    } else {
      draft.value.entityFlagComments.push({ categorySlug, entityTaxId, entityName, comment });
    }
    hasUnsavedChanges.value = true;
  }

  function getEntityFlagComment(categorySlug: string, entityTaxId: string): string {
    return (
      draft.value.entityFlagComments.find(
        (efc) => efc.categorySlug === categorySlug && efc.entityTaxId === entityTaxId
      )?.comment || ''
    );
  }

  function getEntityFlagCommentsByCategory(categorySlug: string): EntityFlagComment[] {
    return draft.value.entityFlagComments.filter((efc) => efc.categorySlug === categorySlug);
  }

  function addAttachment(file: Pick<Attachment, 'name' | 'url'> & Partial<Attachment>): void {
    draft.value.attachments.push({
      id: file.id || file.associationId || file.fileId || crypto.randomUUID(),
      fileId: file.fileId,
      associationId: file.associationId,
      name: file.name,
      sizeBytes: file.sizeBytes,
      localFile: file.localFile,
      url: file.url,
      uploadedAt: file.uploadedAt || new Date().toISOString(),
      uploadedBy: file.uploadedBy,
      description: file.description ?? null,
      categorySlug: file.categorySlug ?? null,
      changeStatus: file.changeStatus ?? (file.localFile ? 'added' : undefined),
    });
    hasUnsavedChanges.value = true;
  }

  function removeAttachment(id: string): void {
    const attachment = draft.value.attachments.find((item) => item.id === id);
    if (attachment?.localFile) {
      URL.revokeObjectURL(attachment.url);
      draft.value.attachments = draft.value.attachments.filter((item) => item.id !== id);
    } else if (attachment) {
      attachment.changeStatus = attachment.changeStatus === 'deleted' ? undefined : 'deleted';
    }
    hasUnsavedChanges.value = true;
  }

  // --- Watchers ---

  // Load from API or storage when requestId changes
  watch(
    requestId,
    async (newId) => {
      if (newId) {
        // Try API first, fall back to localStorage
        const loadedFromApi = await loadFromApi();
        if (!loadedFromApi && !loadFromStorage()) {
          resetDraft();
        }
      }
    },
    { immediate: true }
  );

  // Sync parecerId if provided externally
  watch(
    () => parecerId?.value,
    (newId) => {
      if (newId && newId !== currentParecerId.value) {
        currentParecerId.value = newId;
      }
    },
    { immediate: true }
  );

  // --- Return ---

  return {
    // State
    draft,
    isSaving,
    hasUnsavedChanges,
    currentParecerId,
    currentParecer,
    apiError,
    useLocalStorageFallback,

    // Computed
    flagsNeedingAttention,
    flagsWithEntities,
    validationErrors,
    isValid,
    canSubmit,

    // Methods
    resetDraft,
    saveDraft,
    submitDecision,
    setOutcome,
    setRequestForInformation,
    addCondition,
    removeCondition,
    setFlagComment,
    getFlagComment,
    setEntityFlagComment,
    getEntityFlagComment,
    getEntityFlagCommentsByCategory,
    addAttachment,
    removeAttachment,
    clearStorage,
    loadFromApi,

    // Constants for UI
    REASON_CODES,
    OUTCOME_LABELS,
    RISK_LEVEL_LABELS,
  };
}
